// ══════════════════════════════════════════════════════════════
// Children store — multi-child support on top of localStorage.
//
// Layout:
//   pencilwise-children      = { children: [{id, name, age, createdAt,
//                                            ownerUserId?,
//                                            supabaseStudentId?}],
//                                activeChildId }
//   pencilwise-child-<id>    = full progress object for that child
//
// Owner tagging: each child carries the Supabase user.id of the parent
// who created them. listChildrenForUser() filters by ownership so a
// shared browser doesn't surface Account A's kids to Account B. Children
// created before this tagging existed have no ownerUserId — they're
// claimed by the first signed-in user that lists them
// (claimUnownedChildren).
//
// Cloud linking: each child also carries supabaseStudentId — the row id
// of their record in the Supabase `students` table. Without this link,
// useCloudSync was blindly using s[0] from the user's student list,
// meaning every kid in a multi-child account synced to the same cloud
// row. linkChildToSupabaseStudent() sets the id at create time;
// autoLinkByNameAge() best-effort matches existing local children to
// existing cloud students. clearSupabaseStudentLink() is the inverse —
// the MA-582 mislink-repair primitive a parent-initiated Unlink action
// calls when a local kid's link points at a cloud row this account
// cannot verify as its own (ParentSettingsSections.tsx's SettingsKids).
//
// addChild's ...rest fields: everything past the six canonical fields
// (id, name, age, createdAt, ownerUserId, supabaseStudentId) spreads
// through, but the six canonical fields ALWAYS win — rest is spread
// FIRST, the fixed fields are assigned by name AFTER, so no caller can
// override id/createdAt or shadow the other four via a same-named rest
// key. Any key matching /^kiosk/i is stripped from rest before the
// spread: the family creation path (this function) must stay
// structurally unable to mint a kiosk-shaped entry — that shape is
// reserved for addChildWithId's kiosk callers (kioskIdentity.ts).
//
// Legacy migration: a single child stored under "pencilwise-v4-engine"
// (the old single-child key) is automatically promoted to child[0]
// the first time a multi-child API is called.
// ══════════════════════════════════════════════════════════════

import { removeLocalAttempts } from "./lib/engine/localAttemptStore.ts";
import { removeLocalMilestones } from "./lib/engine/localMilestoneStore.ts";
import { pickLinkRowForChild } from "./App/sharedRowGuard.js";
// Cycle-safe: progress.ts imports this module too, and both sides use the
// other only inside functions (never at module evaluation).
import { isPlaceholderStudentName } from "./App/progress.ts";

const REGISTRY_KEY = "pencilwise-children";
const LEGACY_KEY = "pencilwise-v4-engine";
const childKey = (id) => `pencilwise-child-${id}`;

function uid() {
  return `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function readJSON(key, fallback) {
  try { const r = localStorage.getItem(key); return r ? JSON.parse(r) : fallback; }
  catch { return fallback; }
}
// Returns true when the write landed, false when setItem threw (quota,
// storage disabled). MA-831: the per-child progress save reports it so a
// failed write is detected instead of silently leaving an older record.
function writeJSON(key, val) { try { localStorage.setItem(key, JSON.stringify(val)); return true; } catch { return false; } }

// ── Migration ─────────────────────────────────────────────────
function migrateLegacyIfNeeded() {
  const reg = readJSON(REGISTRY_KEY, null);
  if (reg && Array.isArray(reg.children) && reg.children.length > 0) return reg;
  const legacy = readJSON(LEGACY_KEY, null);
  if (!legacy) return null;
  // Promote legacy single child into the registry as the first (and only) child.
  const id = uid();
  // MA-965: the never-named placeholder (DEFAULT_STUDENT_NAME, any case or
  // surrounding whitespace) and a blank name are not a child's name.
  const name = isPlaceholderStudentName(legacy.studentName) ? "Child 1" : legacy.studentName;
  const age = legacy.studentAge ?? null;
  const newReg = { children: [{ id, name, age, createdAt: Date.now() }], activeChildId: id };
  writeJSON(REGISTRY_KEY, newReg);
  writeJSON(childKey(id), legacy);
  // Keep the legacy key for safety; can be removed once multi-child is stable.
  return newReg;
}

// ── Registry API ──────────────────────────────────────────────
export function listChildren() {
  const reg = migrateLegacyIfNeeded() || readJSON(REGISTRY_KEY, { children: [], activeChildId: null });
  return reg;
}

export function getActiveChildId() {
  const reg = listChildren();
  return reg.activeChildId || null;
}

export function setActiveChildId(id) {
  const reg = listChildren();
  if (!reg.children.some((c) => c.id === id)) return false;
  writeJSON(REGISTRY_KEY, { ...reg, activeChildId: id });
  return true;
}

export function addChild({ name, age, ownerUserId = null, supabaseStudentId = null, ...rest }) {
  // D3/R13 — strip ANY kiosk-shaped key before it can reach the spread below.
  // The family creation path must stay structurally unable to mint a
  // kiosk-tagged entry (isKioskTaggedChild), never merely UI-blocked.
  for (const k of Object.keys(rest)) {
    if (/^kiosk/i.test(k)) delete rest[k];
  }
  const reg = listChildren();
  const id = uid();
  // D4 — rest spread FIRST, then ALL SIX fixed fields by name, so none of
  // them can ever be overridden by a same-named rest key (id/createdAt
  // included, not just the four constructor params).
  const child = {
    ...rest,
    id,
    name: String(name || "Child").trim() || "Child",
    age: age == null ? null : Number(age),
    createdAt: Date.now(),
    ownerUserId: ownerUserId || null,
    supabaseStudentId: supabaseStudentId || null,
  };
  const next = { children: [...reg.children, child], activeChildId: id };
  writeJSON(REGISTRY_KEY, next);
  return id;
}

/**
 * MA-288 kiosk identity — mint (or, if it already exists, update in place)
 * a registry child at an EXPLICIT, caller-supplied id, instead of
 * addChild()'s own randomly minted uid(). The ONLY intended caller is
 * src/lib/teacher/kioskIdentity.ts's ensureKioskChildForEntry: a kiosk
 * roster tile's local registry id must be DETERMINISTIC
 * (`enr_<enrollmentId>`, see kioskChildLocalId) so re-tapping the SAME
 * tile — the same class_enrollments row, possibly across a page reload —
 * resolves to the SAME local child every time, instead of minting a fresh
 * uid() row per tap and orphaning the previous one's local progress. No
 * other call site should use this: every other child-creation path
 * (onboarding, Settings "+ Add child") wants a fresh random id and should
 * keep calling addChild().
 *
 * Idempotent: if a child with `id` already exists, this UPDATEs its fields
 * in place via updateChildMeta (a claimed<->unclaimed enrollment transition
 * repoints supabaseStudentId/kioskClaimedStudentId this way) instead of
 * inserting a duplicate row. Unlike addChild, this never touches
 * activeChildId — ensureKioskChildForEntry decides separately whether/when
 * to actually switch (via app.pickChild), and a mere "ensure this tile's
 * local record exists" call must not have that side effect.
 *
 * @param {{id: string, name?: string, age?: (number|null), ownerUserId?: (string|null), supabaseStudentId?: (string|null), [extra: string]: unknown}} fields
 * @returns {string}
 */
export function addChildWithId({ id, name, age = null, ownerUserId = null, supabaseStudentId = null, ...rest }) {
  if (!id) throw new Error("addChildWithId requires an explicit id");
  const reg = listChildren();
  const patch = {
    name: String(name || "Child").trim() || "Child",
    age: age == null ? null : Number(age),
    ownerUserId: ownerUserId || null,
    supabaseStudentId: supabaseStudentId || null,
    ...rest,
  };
  const existing = reg.children.find((c) => c.id === id);
  if (existing) {
    updateChildMeta(id, patch);
    return id;
  }
  const child = { id, createdAt: Date.now(), ...patch };
  writeJSON(REGISTRY_KEY, { ...reg, children: [...reg.children, child] });
  return id;
}

/**
 * Set the supabaseStudentId on a local child. Used right after
 * createCloudStudent succeeds during onboarding so subsequent syncs
 * write to the right cloud row.
 */
export function linkChildToSupabaseStudent(childId, supabaseStudentId) {
  if (!childId || !supabaseStudentId) return false;
  const reg = listChildren();
  const idx = reg.children.findIndex((c) => c.id === childId);
  if (idx < 0) return false;
  if (reg.children[idx].supabaseStudentId === supabaseStudentId) return true;
  // MA-800: structural guard — a cloud row already linked to a DIFFERENT local
  // child is never linked a second time (shared attempts/milestones/sessions).
  if (reg.children.some((c) => c.id !== childId && c.supabaseStudentId === supabaseStudentId)) {
    console.warn("[childrenStore] refused to link a cloud student row already linked to another local child");
    return false;
  }
  const next = {
    ...reg,
    children: reg.children.map((c) => (c.id === childId ? { ...c, supabaseStudentId } : c)),
  };
  writeJSON(REGISTRY_KEY, next);
  return true;
}

/**
 * MA-582 mislink-repair — the inverse of linkChildToSupabaseStudent(): clear
 * a local child's cloud link so its progress becomes local-only. The
 * parent-initiated "Unlink" repair for a kid whose link resolves to a cloud
 * row this account cannot verify as its own (deriveKidLinkState's
 * "unverified" kind, src/screens/kidLinkRepair.ts).
 *
 * Own findIndex existence check (never a blind updateChildMeta write) —
 * returns false on an unknown id rather than silently no-op writing the
 * registry. Returns true when the link is already null, mirroring
 * linkChildToSupabaseStudent's own early-true above (both treat "already at
 * the target state" as a successful no-op, not a failure).
 *
 * Refuses kiosk-tagged entries (isKioskTaggedChild) — a kiosk link is the
 * armed-device roster identity, not a family link, and the rejected
 * blanket-repair's kiosk-wipe hazard (see App.jsx's ownedStudentIds
 * comment) must stay impossible at the primitive level, not merely gated
 * in the UI that happens to call this today.
 *
 * Deliberately narrow: never touches activeChildId or any per-child
 * progress/attempt/milestone key. Callers that need the active-pointer to
 * self-correct after an unlink (App.jsx's clearKidCloudLink) do that
 * themselves — this function's only job is the registry write.
 */
export function clearSupabaseStudentLink(childId) {
  if (!childId) return false;
  const reg = listChildren();
  const idx = reg.children.findIndex((c) => c.id === childId);
  if (idx < 0) return false;
  const child = reg.children[idx];
  if (isKioskTaggedChild(child)) return false;
  if (child.supabaseStudentId == null) return true;
  const next = {
    ...reg,
    children: reg.children.map((c) => (c.id === childId ? { ...c, supabaseStudentId: null } : c)),
  };
  writeJSON(REGISTRY_KEY, next);
  return true;
}

/**
 * Best-effort match existing local children (no supabaseStudentId yet)
 * against the user's cloud students by name + age. Only sets the link
 * when the match is unambiguous — never silently misroutes a kid to
 * the wrong cloud record. Returns the count of links established.
 */
export function autoLinkByNameAge(userId, cloudStudents) {
  if (!userId || !Array.isArray(cloudStudents)) return 0;
  const reg = listChildren();
  let linked = 0;
  // Track which cloud ids we've already linked, so we don't claim the
  // same one twice when two local kids share a name.
  const claimed = new Set();
  const next = reg.children.map((c) => {
    if (c.supabaseStudentId) return c;
    if (c.ownerUserId && c.ownerUserId !== userId) return c;
    // MA-800: pickLinkRowForChild never offers a row the registry already links
    // to ANOTHER local child, and decides ambiguity over ALL same-name rows (an
    // orphan duplicate must not become "unambiguous" because its twin is taken).
    const pick = pickLinkRowForChild(reg, cloudStudents, c, claimed);
    if (!pick) return c; // ambiguous — leave unset rather than mis-route
    claimed.add(pick.id);
    linked += 1;
    return { ...c, supabaseStudentId: pick.id };
  });
  if (linked > 0) writeJSON(REGISTRY_KEY, { ...reg, children: next });
  return linked;
}

/**
 * Filter the registry to only the children owned by `userId`. Children
 * with no ownerUserId are visible to all (transitional state — the next
 * call to claimUnownedChildren tags them).
 *
 * Deliberately does NOT exclude kiosk-tagged entries (see
 * isKioskTaggedChild/listFamilyChildrenForUser below) — this is the
 * function App.jsx uses to populate the React `registry` state that
 * activeChild resolution (`registry.children.find(id === activeChildId)`)
 * depends on, and that lookup MUST keep seeing a kiosk-tapped identity
 * right after app.pickChild selects it (play.who.tsx's kiosk flow), or
 * kiosk gameplay breaks the instant a tile is tapped. The registry keeps
 * ALL owned entries; family-facing UI reads through the narrower
 * listFamilyChildrenForUser instead.
 */
export function listChildrenForUser(userId) {
  const reg = listChildren();
  if (!userId) return { children: [], activeChildId: null };
  const visible = reg.children.filter((c) => !c.ownerUserId || c.ownerUserId === userId);
  // If the active child is no longer visible to this user, drop it.
  const activeChildId = visible.some((c) => c.id === reg.activeChildId) ? reg.activeChildId : null;
  return { children: visible, activeChildId };
}

/**
 * MA-553 — true when this registry entry was created by a kiosk roster tap.
 * kioskIdentity.ts's ensureKioskChildForEntry tags every entry it writes
 * with a truthy `kioskEnrollmentId`; every other creation path (addChild's
 * onboarding flow, addChildWithId's non-kiosk callers — there are none
 * today, but the field is what distinguishes the shape, not the caller)
 * never sets it. Exported so every family-facing consumer applies the SAME
 * predicate instead of re-deriving "is this a kiosk identity" independently
 * in App.jsx and account.ts.
 */
export function isKioskTaggedChild(child) {
  return !!(child && child.kioskEnrollmentId);
}

/**
 * MA-553 — the FAMILY-facing twin of listChildrenForUser: same ownership
 * filter, PLUS excludes kiosk-tapped roster identities. THE BUG this closes:
 * a kiosk roster tap (ensureKioskChildForEntry -> addChildWithId) writes a
 * local-registry child tagged `kioskEnrollmentId`, owned by the signed-in
 * teacher; listChildrenForUser's owner-only filter let it straight through
 * into the family kid picker (WhosPlayingScreen) and Settings -> Kids
 * (useKids, src/lib/account.ts), and it never got cleaned up on device
 * disarm (see removeKioskTaggedChildren below).
 *
 * This mirrors supabaseClient.js's fetchStudents(), which already excludes
 * enrollment-linked rows from the cloud parent read via
 * `.is('enrollment_id', null)` (PR #1716 lineage, the CLAUDE.md-documented
 * shadow-exclusion filter also pinned by kioskIdentity.test.mjs's
 * "bleed guard" case) — that filter is the cloud read path's guard against
 * exactly this bleed; the local registry never had the equivalent, which is
 * the MA-553 gap. listChildrenForUser itself is left unfiltered (see its
 * own comment above) — this is the separate, explicitly-named family read
 * path callers must opt into.
 */
export function listFamilyChildrenForUser(userId) {
  const base = listChildrenForUser(userId);
  const children = base.children.filter((c) => !isKioskTaggedChild(c));
  const activeChildId = children.some((c) => c.id === base.activeChildId) ? base.activeChildId : null;
  return { children, activeChildId };
}

/**
 * Claim every child without an ownerUserId for the given user. Called
 * once per user-id change so children created before owner-tagging or
 * by anonymous flows get permanently bound to the first signed-in user
 * who sees them.
 */
export function claimUnownedChildren(userId) {
  if (!userId) return 0;
  const reg = listChildren();
  let claimed = 0;
  const next = reg.children.map((c) => {
    if (!c.ownerUserId) { claimed++; return { ...c, ownerUserId: userId }; }
    return c;
  });
  if (claimed > 0) writeJSON(REGISTRY_KEY, { ...reg, children: next });
  return claimed;
}

export function updateChildMeta(id, patch) {
  const reg = listChildren();
  const next = { ...reg, children: reg.children.map((c) => (c.id === id ? { ...c, ...patch } : c)) };
  writeJSON(REGISTRY_KEY, next);
}

export function removeChild(id) {
  const reg = listChildren();
  const remaining = reg.children.filter((c) => c.id !== id);
  const nextActive = reg.activeChildId === id ? (remaining[0]?.id || null) : reg.activeChildId;
  writeJSON(REGISTRY_KEY, { children: remaining, activeChildId: nextActive });
  try { localStorage.removeItem(childKey(id)); } catch {}
  // B-2/G-6: also drop the local attempt + milestone mirrors so a deleted
  // kid leaves no orphaned performance history on the device (retention /
  // resurface-on-id-collision gap).
  removeLocalAttempts(id);
  removeLocalMilestones(id);
}

/**
 * MA-553 — purge every kiosk-tagged registry entry (plus its per-child
 * progress/attempt/milestone mirrors, via removeChild). The intended
 * caller is kioskArming.ts's disarmDevice(): a kiosk-tapped identity
 * (isKioskTaggedChild) is otherwise created once and never cleaned up —
 * it survives disarm forever, accumulating in the registry on every
 * re-arm/re-tap cycle.
 *
 * SCOPING DECISION — unscoped by owner or class, deliberately:
 * disarmDevice() is called with zero arguments at both its real call
 * sites (teacher.tsx's disarm button, KioskRosterScreen's exit action),
 * and kioskArming.ts's own header documents arming as PER-DEVICE state
 * that is NOT scoped to whichever account happens to be signed in ("a
 * classroom iPad stays armed across whoever is currently signed into the
 * teacher session on it") — so there is no current-user id available at
 * the disarm call site without widening disarmDevice()'s signature and
 * touching both its callers, out of this fix's scope. Per-class scoping
 * isn't available either: a registry entry carries only
 * `kioskEnrollmentId`, never the enrollment's class_id, so "purge only
 * this class's entries" would need a roster lookup the disarm call site
 * doesn't have. Purging every kiosk-tagged entry on disarm has no
 * false-positive case to guard against: kiosk-tagged entries exist ONLY
 * as a side effect of a roster tap on THIS device (kioskIdentity.ts's
 * ensureKioskChildForEntry is their one writer), registry storage is
 * per-device (localStorage, not shared across devices), and the filter
 * below (isKioskTaggedChild) guarantees an untagged family child is never
 * touched.
 *
 * Reuses removeChild() per id rather than a bespoke delete, so the same
 * progress/local-attempt/local-milestone cleanup the ordinary delete path
 * already gets (this file's own removeChild, B-2/G-6) applies here too —
 * no orphaned per-kid storage left behind on disarm.
 *
 * Returns the number of entries purged (0 when the device holds none).
 */
export function removeKioskTaggedChildren() {
  const reg = listChildren();
  const ids = reg.children.filter(isKioskTaggedChild).map((c) => c.id);
  for (const id of ids) removeChild(id);
  return ids.length;
}

/**
 * MA-925 (D-TUX-6) — after a SUCCESSFUL kiosk roster read, remove every
 * kiosk-tagged local child whose enrollment is no longer on that roster (a
 * child the teacher removed keeps no record on the class device). Only
 * kiosk-tagged entries are ever touched (isKioskTaggedChild), so a family
 * child is never removed; an untagged entry with a matching id is left alone.
 * The caller passes the enrollment ids of a roster read that SUCCEEDED; it
 * must never call this after a failed read (an empty list would purge every
 * kiosk child). Reuses removeChild per id, so the per-child progress,
 * attempt and milestone mirrors go too. Returns the ids removed.
 */
export function pruneKioskChildrenAbsentFromRoster(rosterEnrollmentIds) {
  if (!Array.isArray(rosterEnrollmentIds)) return [];
  const keep = new Set(rosterEnrollmentIds.map(String));
  const reg = listChildren();
  const ids = reg.children
    .filter((c) => isKioskTaggedChild(c) && !keep.has(String(c.kioskEnrollmentId)))
    .map((c) => c.id);
  for (const id of ids) removeChild(id);
  return ids;
}

// ── Per-child progress API ────────────────────────────────────
export function loadProgressForChild(id, defaultProgress) {
  if (!id) return defaultProgress();
  const stored = readJSON(childKey(id), null);
  return stored ? { ...defaultProgress(), ...stored } : defaultProgress();
}

// MA-831: returns true when the record was stored, false when it was not (no
// id, or the storage write threw). Callers that ignore the result are unchanged.
export function saveProgressForChild(id, progress) {
  if (!id) return false;
  return writeJSON(childKey(id), progress);
}

export function resetProgressForChild(id) {
  if (!id) return;
  try { localStorage.removeItem(childKey(id)); } catch {}
}
